// frontend/modules/auth/stores/auth-store.ts
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { AuthUser } from '@/shared/api/model';
import { getUser } from '@/shared/api/client/authentication';
import { customInstance } from '@/shared/api/mutator/custom-instance';

interface AuthState {
    user: AuthUser | null;
    isLoading: boolean;
    error: Error | null;
    setUser: (user: AuthUser | null) => void;
    setLoading: (isLoading: boolean) => void;
    setError: (error: Error | null) => void;
    clearAuth: () => void;
    refreshUser: () => Promise<void>;
    switchBranch: (branchCode: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set) => ({
            user: null,
            isLoading: false,
            error: null,
            setUser: (user) => set({ user, error: null }),
            setLoading: (isLoading) => set({ isLoading }),
            setError: (error) => set({ error }),
            clearAuth: () => {
                localStorage.removeItem('auth-storage');
                set({ user: null, error: null, isLoading: false });
            },
            refreshUser: async () => {
                set({ isLoading: true, error: null });
                try {
                    const userData = await getUser();
                    set({ user: userData, isLoading: false });
                } catch (err: any) {
                    set({ error: err, isLoading: false });
                    throw err;
                }
            },
            switchBranch: async (branchCode: string) => {
                set({ isLoading: true, error: null });
                try {
                    const userData = await customInstance<AuthUser>({
                        url: '/api/frontend/auth/switch-branch',
                        method: 'POST',
                        data: { branchCode },
                    });
                    set({ user: userData, isLoading: false });
                } catch (err: any) {
                    set({ error: err, isLoading: false });
                    throw err;
                }
            },
        }),
        {
            name: 'auth-storage',
            // Only persist user data, not loading/error states
            partialize: (state) => ({ user: state.user })
        }
    )
);